import { useState, type FormEvent } from "react";

import type { Customer, Operator } from "@attendik/types/admin-schemas";

import { api } from "../api/client.js";
import { isApiError } from "../api/errors.js";
import { PageHeader } from "../components/AppLayout.js";
import { Button } from "../components/ui/button.js";
import { Card } from "../components/ui/card.js";
import {
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader
} from "../components/ui/dialog.js";
import { Input } from "../components/ui/input.js";
import { Label } from "../components/ui/label.js";
import { Select } from "../components/ui/select.js";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../components/ui/table.js";
import { EmptyState, ErrorState, LoadingState } from "../components/states.js";
import { useAsync } from "../lib/useAsync.js";
import { useScope } from "../store/auth.js";
import { StatusBadge, accountStatusVariant } from "./shared/pills.js";

interface OperatorsData {
  operators: Operator[];
  customers: Customer[];
}

/** Operators page — list, create, reset password, disable/enable (P2-T9). */
export function OperatorsPage(): JSX.Element {
  const { isPortalAdmin, customerId } = useScope();
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState<Operator | null>(null);
  const [disabling, setDisabling] = useState<Operator | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const { data, loading, error, reload } = useAsync<OperatorsData>(async () => {
    const operators = await api.listOperators();
    const customers = isPortalAdmin ? await api.listCustomers() : [];
    return { operators, customers };
  }, [isPortalAdmin]);

  const customerName = (id: string): string =>
    data?.customers.find((c) => c.id === id)?.name ?? "—";

  const confirmDisable = async (): Promise<void> => {
    if (!disabling) {
      return;
    }
    setActionError(null);
    try {
      await api.updateOperator(disabling.id, {
        status: disabling.status === "ACTIVE" ? "DISABLED" : "ACTIVE"
      });
      setDisabling(null);
      reload();
    } catch (err) {
      setActionError(
        isApiError(err) ? err.message : "Could not update the operator."
      );
    }
  };

  return (
    <>
      <PageHeader
        title="Operators"
        description="Door operators who scan badges in the scan app."
        actions={<Button onClick={() => setCreating(true)}>Create operator</Button>}
      />

      {actionError ? (
        <p role="alert" className="mb-3 text-sm text-destructive">
          {actionError}
        </p>
      ) : null}

      <Card>
        {loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState message={error} onRetry={reload} />
        ) : !data || data.operators.length === 0 ? (
          <EmptyState
            title="No operators"
            description="Create an operator to assign them to events."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                {isPortalAdmin ? <TableHead>Customer</TableHead> : null}
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.operators.map((operator) => (
                <TableRow key={operator.id}>
                  <TableCell className="font-medium">{operator.name}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {operator.email}
                  </TableCell>
                  {isPortalAdmin ? (
                    <TableCell className="text-muted-foreground">
                      {customerName(operator.customer_id)}
                    </TableCell>
                  ) : null}
                  <TableCell>
                    <StatusBadge variant={accountStatusVariant(operator.status)}>
                      {operator.status}
                    </StatusBadge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setResetting(operator)}
                      >
                        Reset password
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setDisabling(operator)}
                      >
                        {operator.status === "ACTIVE" ? "Disable" : "Enable"}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {creating ? (
        <CreateOperatorDialog
          customers={data?.customers ?? []}
          ownCustomerId={customerId}
          isPortalAdmin={isPortalAdmin}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            reload();
          }}
        />
      ) : null}

      {resetting ? (
        <ResetPasswordDialog
          subjectName={resetting.name}
          onClose={() => setResetting(null)}
          onSubmit={(pw) => api.resetOperatorPassword(resetting.id, pw)}
          onDone={() => setResetting(null)}
        />
      ) : null}

      {disabling ? (
        <Dialog
          open
          onClose={() => setDisabling(null)}
          labelledBy="disable-operator-title"
        >
          <DialogHeader
            id="disable-operator-title"
            title={
              disabling.status === "ACTIVE"
                ? "Disable operator"
                : "Enable operator"
            }
            onClose={() => setDisabling(null)}
          />
          <DialogBody>
            {disabling.status === "ACTIVE" ? (
              <p className="text-sm text-muted-foreground">
                Disabling <strong>{disabling.name}</strong> cuts their refresh
                tokens immediately. Any active scan session remains valid for up
                to 24 hours until its access token expires.
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                Re-enable <strong>{disabling.name}</strong> so they can sign in
                again.
              </p>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisabling(null)}>
              Cancel
            </Button>
            <Button onClick={confirmDisable}>
              {disabling.status === "ACTIVE" ? "Disable" : "Enable"}
            </Button>
          </DialogFooter>
        </Dialog>
      ) : null}
    </>
  );
}

function CreateOperatorDialog({
  customers,
  ownCustomerId,
  isPortalAdmin,
  onClose,
  onCreated
}: {
  customers: Customer[];
  ownCustomerId: string | null;
  isPortalAdmin: boolean;
  onClose: () => void;
  onCreated: () => void;
}): JSX.Element {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(
    isPortalAdmin ? (customers[0]?.id ?? "") : (ownCustomerId ?? "")
  );
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (password.length < 12) {
      setError("Password must be at least 12 characters.");
      return;
    }
    const customerForOperator = isPortalAdmin ? selectedCustomer : ownCustomerId;
    if (!customerForOperator) {
      setError("A customer is required.");
      return;
    }
    setSubmitting(true);
    try {
      await api.createOperator({
        customer_id: customerForOperator,
        email: email.trim(),
        name: name.trim(),
        password
      });
      onCreated();
    } catch (err) {
      if (isApiError(err) && err.code === "EMAIL_TAKEN") {
        setError("An operator with that email already exists.");
      } else {
        setError("Could not create the operator. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={onClose} labelledBy="create-operator-title">
      <DialogHeader
        id="create-operator-title"
        title="Create operator"
        onClose={onClose}
      />
      <form onSubmit={handleSubmit}>
        <DialogBody>
          {isPortalAdmin ? (
            <div className="space-y-1.5">
              <Label htmlFor="op-customer">Customer</Label>
              <Select
                id="op-customer"
                value={selectedCustomer}
                onChange={(e) => setSelectedCustomer(e.target.value)}
                required
              >
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </div>
          ) : null}
          <div className="space-y-1.5">
            <Label htmlFor="op-name">Name</Label>
            <Input
              id="op-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="op-email">Email</Label>
            <Input
              id="op-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="op-password">Initial password</Label>
            <Input
              id="op-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <p className="text-xs text-muted-foreground">Minimum 12 characters.</p>
          </div>
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}

/** Reusable reset-password dialog — shared by Operators and Admin Users. */
export function ResetPasswordDialog({
  subjectName,
  onClose,
  onSubmit,
  onDone
}: {
  subjectName: string;
  onClose: () => void;
  onSubmit: (newPassword: string) => Promise<void>;
  onDone: () => void;
}): JSX.Element {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (password.length < 12) {
      setError("Password must be at least 12 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(password);
      onDone();
    } catch (err) {
      setError(isApiError(err) ? err.message : "Could not reset the password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={onClose} labelledBy="reset-password-title">
      <DialogHeader
        id="reset-password-title"
        title="Reset password"
        description={`Set a new password for ${subjectName}.`}
        onClose={onClose}
      />
      <form onSubmit={handleSubmit}>
        <DialogBody>
          <div className="space-y-1.5">
            <Label htmlFor="reset-password">New password</Label>
            <Input
              id="reset-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <p className="text-xs text-muted-foreground">Minimum 12 characters.</p>
          </div>
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? "Saving…" : "Reset password"}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
